import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {type SortOption} from '../hooks/useSharedViewsItems';
import {type WorkspacePreferences} from '../hooks/useWorkspacePreferences';
import {type NamedWorkspaceMenuItem} from '../types';
import {WANDB_WORKSPACES_DOCS} from '../utils';
import {NamedWorkspaceMenuGroup} from './NamedWorkspaceMenuGroup';
import {NamedWorkspaceSortOptionSelector} from './NamedWorkspaceSortOptionSelector';

export const SAVED_VIEWS_INFO_CONTENT =
  'Views are workspaces saved by you or your teammates. Use views for future reference or easy collaborative analysis.';

type NamedWorkspaceSharedViewsProps = {
  sharedViewsItems: NamedWorkspaceMenuItem[];
  sortOption: SortOption;
  shouldShowMyViewsOnly: boolean;
  debouncedSearch: string;
  shouldShowInfo: boolean;
  setShouldShowInfo: (value: any) => void;
  updatePreference: (key: keyof WorkspacePreferences, value: any) => void;
};

export const NamedWorkspaceSharedViews = ({
  sharedViewsItems,
  sortOption,
  shouldShowMyViewsOnly,
  debouncedSearch,
  shouldShowInfo,
  setShouldShowInfo,
  updatePreference,
}: NamedWorkspaceSharedViewsProps) => {
  const sharedViewsCount = sharedViewsItems.length;

  return (
    <>
      <DropdownMenu.Label
        data-test="shared-view-label"
        className="mb-8 mt-16 flex flex-wrap items-center justify-between gap-16 px-12">
        <div className="flex gap-8 text-base font-semibold leading-[24px]">
          <p>Saved views</p>
          {debouncedSearch ||
          (shouldShowMyViewsOnly && sharedViewsCount > 0) ? (
            <div className="rounded bg-wb-surface-sunken px-6 text-sm">
              {`${sharedViewsItems.length} of ${sharedViewsCount}`}
            </div>
          ) : (
            <div className="text-sm text-wb-text-tertiary">
              {sharedViewsCount}
            </div>
          )}
        </div>
        <div
          className={twMerge(
            'flex cursor-pointer items-center justify-center rounded',
            'text-wb-text-tertiary'
          )}>
          <Button
            variant="ghost"
            icon="info"
            onClick={() => {
              setShouldShowInfo((prev: boolean) => !prev);
            }}
            className={twMerge(
              'h-32 w-32 p-8',
              'text-wb-text-primary hover:text-wb-text-link dark:text-moon-300 dark:hover:text-moon-100'
            )}
          />
          <NamedWorkspaceSortOptionSelector
            selectedOption={sortOption}
            onSelectOption={e => updatePreference('sortOption', e)}
            shouldShowMyViewsOnly={shouldShowMyViewsOnly}
            onShowMyViewsOnly={value =>
              updatePreference('showMyViewsOnly', value)
            }
          />
        </div>
      </DropdownMenu.Label>
      {shouldShowInfo && (
        <div className="flex w-full justify-center">
          <p
            className={twMerge(
              'mx-12 mb-8 h-full w-full rounded px-12 py-8 text-sm',
              'bg-wb-surface-sunken text-wb-text-tertiary dark:bg-moonbeam/[.05]'
            )}>
            {SAVED_VIEWS_INFO_CONTENT}{' '}
            <span>
              <TargetBlank
                href={WANDB_WORKSPACES_DOCS}
                className="font-semibold text-wb-text-link hover:text-wb-text-link-hover">
                Learn more about views
              </TargetBlank>
            </span>{' '}
            in Docs.
          </p>
        </div>
      )}
      <NamedWorkspaceMenuGroup options={sharedViewsItems} defaultMaxItems={5} />
    </>
  );
};
